/**
 * NFR-PWA-004 — stage SW activation behind a safe-state checkpoint.
 * App listens for `veto:prepare-update` and replies with `veto:safe-to-update`.
 */
import { listQueuedOutbox } from "@veto/storage";

export interface UpdateCheckpointResult {
  safe: boolean;
  pendingOutbox: number;
  reason?: string;
}

const PREPARE = "veto:prepare-update";
const SAFE = "veto:safe-to-update";

/** App-side: wire once while unlocked. */
export function bindUpdateCheckpoint(): () => void {
  const onPrepare = () => {
    void (async () => {
      try {
        const pending = await listQueuedOutbox();
        const active = pending.filter(
          (m) => m.status === "queued" || m.status === "sent" || m.status === "draft",
        );
        window.dispatchEvent(
          new CustomEvent(SAFE, {
            detail: {
              safe: active.length === 0,
              pendingOutbox: active.length,
              reason:
                active.length > 0
                  ? `${active.length} outgoing message(s) still syncing`
                  : undefined,
            } satisfies UpdateCheckpointResult,
          }),
        );
      } catch {
        window.dispatchEvent(
          new CustomEvent(SAFE, {
            detail: { safe: true, pendingOutbox: 0 } satisfies UpdateCheckpointResult,
          }),
        );
      }
    })();
  };
  window.addEventListener(PREPARE, onPrepare);
  return () => window.removeEventListener(PREPARE, onPrepare);
}

/** Prompt-side: ask the app whether it is safe to skipWaiting. */
export function requestSafeUpdateCheckpoint(timeoutMs = 2500): Promise<UpdateCheckpointResult> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (detail: UpdateCheckpointResult) => {
      if (settled) return;
      settled = true;
      window.removeEventListener(SAFE, onSafe as EventListener);
      window.clearTimeout(timer);
      resolve(detail);
    };
    const onSafe = (e: Event) => {
      const detail = (e as CustomEvent<UpdateCheckpointResult>).detail;
      finish(detail ?? { safe: true, pendingOutbox: 0 });
    };
    window.addEventListener(SAFE, onSafe as EventListener);
    window.dispatchEvent(new CustomEvent(PREPARE));
    const timer = window.setTimeout(() => {
      // No app listener (locked / loading) — allow update.
      finish({ safe: true, pendingOutbox: 0 });
    }, timeoutMs);
  });
}
